import React from 'react';

const policySections = [
  {
    id: 1,
    title: 'Employment Contracts & Employment Status',
    intro:
      'Employers should clearly document the employment relationship from day one and make sure each worker understands their legal status and related rights.',
    blocks: [
      {
        heading: 'Written Statement of Employment',
        body:
          'Employers must provide employees with a written statement of employment particulars on or before their first working day.',
        bullets: [
          'Job title and description',
          'Start date and continuous service date',
          'Salary or hourly rate',
          'Pay frequency',
          'Working hours',
          'Holiday entitlement',
          'Place of work',
          'Probation period',
          'Notice periods',
          'Benefits',
        ],
      },
      {
        heading: 'Employment Status',
        body: 'The organisation should clearly define the worker category and the rights linked to it.',
        bullets: ['Employee', 'Worker', 'Self-employed contractor'],
      },
    ],
  },
  {
    id: 2,
    title: 'Recruitment and Hiring Policy',
    intro:
      'Recruitment should be fair, consistent, and free from discrimination, with clear standards for assessment, record keeping, and right to work verification.',
    blocks: [
      {
        heading: 'Equal Opportunity Recruitment',
        body: 'The organisation must not discriminate based on any protected characteristic.',
        bullets: [
          'Age',
          'Disability',
          'Gender reassignment',
          'Marriage or civil partnership',
          'Pregnancy or maternity',
          'Race',
          'Religion or belief',
          'Sex',
          'Sexual orientation',
        ],
      },
      {
        heading: 'Recruitment Process',
        bullets: [
          'Job advertising procedures',
          'Application review process',
          'Interview process',
          'Interview scoring',
          'Candidate data protection',
          'Background checks where appropriate',
          'Right to work verification',
        ],
      },
      {
        heading: 'Right to Work Checks',
        body: 'Employers must verify that all employees have the legal right to work in the UK.',
      },
    ],
  },
  {
    id: 3,
    title: 'Probationary Period Policy',
    intro: 'Most new employees will complete a probationary period to assess suitability and performance in role.',
    blocks: [
      {
        heading: 'Typical probation period',
        bullets: ['3 to 6 months'],
      },
      {
        heading: 'During probation the organisation may',
        bullets: [
          'Conduct performance reviews',
          'Extend the probation period',
          'Terminate employment with shorter notice',
        ],
        body: 'Performance expectations should be communicated clearly and monitored consistently.',
      },
    ],
  },
  {
    id: 4,
    title: 'Working Hours Policy',
    intro: 'Working time arrangements should balance operational requirements with employee wellbeing and legal compliance.',
    blocks: [
      {
        heading: 'Standard Working Hours',
        bullets: ['37.5 to 40 hours per week', 'Monday to Friday unless otherwise agreed'],
      },
      {
        heading: 'Maximum Working Time',
        body: 'The average working week must not exceed 48 hours unless the employee has voluntarily opted out.',
        highlight: '48 hours maximum average working week',
      },
      {
        heading: 'Rest Breaks',
        bullets: [
          '20 minutes rest break when working over 6 hours',
          '11 hours rest between working days',
          '24 hours uninterrupted rest per week',
        ],
      },
    ],
  },
  {
    id: 5,
    title: 'Attendance and Time Recording',
    intro:
      'Employees should record working time accurately using the organisation’s approved systems.',
    blocks: [
      {
        heading: 'Approved time recording methods',
        bullets: ['Clock-in / clock-out', 'Shift logging', 'Project time tracking'],
        body: 'Employees are responsible for ensuring time records are accurate and up to date.',
      },
    ],
  },
  {
    id: 6,
    title: 'Overtime Policy',
    intro: 'Overtime may be required depending on operational needs and should be controlled consistently.',
    blocks: [
      {
        heading: 'Key rules',
        bullets: [
          'Overtime must be approved by a manager',
          'Overtime may be paid or taken as time off in lieu (TOIL)',
          'Overtime rates should be defined by company policy',
        ],
      },
    ],
  },
  {
    id: 7,
    title: 'Holiday / Annual Leave Policy',
    intro: 'Annual leave rules should explain entitlement, booking processes, and any carry-over limits.',
    blocks: [
      {
        heading: 'Annual Leave Entitlement',
        body:
          'Employees are entitled to 5.6 weeks paid annual leave per year. For full-time employees working 5 days per week this equals 28 days per year.',
        highlight: '5.6 weeks / 28 days for a standard full-time worker',
      },
      {
        heading: 'Holiday Booking',
        body:
          'Employees should request leave in advance through the company HR system. Managers may approve or reject requests based on operational requirements.',
      },
      {
        heading: 'Carry Over',
        body: 'Organisations may allow limited carry-over of unused leave into the next holiday year.',
        bullets: ['Common policy: maximum carry-over of 5 days'],
      },
    ],
  },
  {
    id: 8,
    title: 'Public Holiday Policy',
    intro: 'The organisation may observe the standard UK public holidays, depending on business needs and contractual terms.',
    blocks: [
      {
        heading: 'Typical UK public holidays',
        bullets: [
          "New Year's Day",
          'Good Friday',
          'Easter Monday',
          'Early May Bank Holiday',
          'Spring Bank Holiday',
          'Summer Bank Holiday',
          'Christmas Day',
          'Boxing Day',
        ],
      },
    ],
  },
  {
    id: 9,
    title: 'Sick Leave Policy',
    intro: 'Sickness procedures should help employees report absence promptly and understand certification requirements.',
    blocks: [
      {
        heading: 'Reporting Sickness',
        body: 'Employees should notify their manager as soon as possible when they are unable to attend work due to illness.',
      },
      {
        heading: 'Self-Certification',
        body: 'Employees may self-certify sickness absence for up to 7 calendar days.',
        highlight: 'Self-certification for up to 7 calendar days',
      },
      {
        heading: 'Medical Certificates',
        body: 'For sickness lasting longer than 7 days, a medical certificate (fit note) is required.',
      },
      {
        heading: 'Statutory Sick Pay',
        body: 'Eligible employees may receive Statutory Sick Pay in line with current UK rules.',
      },
    ],
  },
  {
    id: 10,
    title: 'Return to Work Procedure',
    intro: 'A return-to-work process can support wellbeing and identify any adjustments needed after absence.',
    blocks: [
      {
        heading: 'Topics that may be covered',
        bullets: ['Employee wellbeing', 'Support needs', 'Any required adjustments'],
      },
    ],
  },
  {
    id: 11,
    title: 'Flexible Working Policy',
    intro: 'Employees may request flexible working arrangements, which should be reviewed fairly and in line with business needs.',
    blocks: [
      {
        heading: 'Examples',
        bullets: ['Remote work', 'Compressed hours', 'Job sharing', 'Adjusted working hours'],
      },
    ],
  },
  {
    id: 12,
    title: 'Parental Leave Policies',
    intro: 'The organisation should support statutory family-related leave entitlements.',
    blocks: [
      {
        heading: 'Typical entitlements',
        bullets: [
          'Maternity Leave',
          'Paternity Leave',
          'Adoption Leave',
          'Shared Parental Leave',
          'Parental Leave',
          'Time off for dependants',
        ],
      },
    ],
  },
  {
    id: 13,
    title: 'Equality, Diversity and Inclusion',
    intro: 'The organisation is committed to a workplace that is inclusive, respectful, and free from discrimination.',
    blocks: [
      {
        heading: 'Expected standard',
        body: 'Employees should treat colleagues with respect, fairness, and professionalism at all times.',
      },
    ],
  },
  {
    id: 14,
    title: 'Anti-Harassment and Bullying Policy',
    intro: 'Harassment, bullying, and victimisation should never be tolerated and should be addressed promptly.',
    blocks: [
      {
        heading: 'Reporting routes',
        bullets: ['HR', 'Management', 'Confidential reporting channels'],
        body: 'Concerns should be investigated promptly and fairly.',
      },
    ],
  },
  {
    id: 15,
    title: 'Health and Safety Policy',
    intro: 'The organisation should take reasonable steps to maintain a safe working environment.',
    blocks: [
      {
        heading: 'Employee responsibilities',
        bullets: ['Follow safety procedures', 'Report hazards', 'Cooperate with health and safety measures'],
        body: 'Risk assessments should be carried out where appropriate.',
      },
    ],
  },
  {
    id: 16,
    title: 'Remote Working Policy',
    intro: 'Where remote working is permitted, expectations should be clear and practical.',
    blocks: [
      {
        heading: 'Key expectations',
        bullets: ['Maintain productivity', 'Protect company data', 'Follow IT security procedures'],
      },
    ],
  },
  {
    id: 17,
    title: 'IT and Acceptable Use Policy',
    intro: 'Company systems should be used responsibly and in line with security and conduct standards.',
    blocks: [
      {
        heading: 'Prohibited activities may include',
        bullets: ['Illegal downloads', 'Accessing inappropriate material', 'Misuse of company networks'],
      },
    ],
  },
  {
    id: 18,
    title: 'Data Protection and Confidentiality',
    intro: 'Employee and company data should be handled securely and only for authorised business purposes.',
    blocks: [
      {
        heading: 'Employees should',
        bullets: [
          'Protect confidential information',
          'Follow company data policies',
          'Avoid unauthorised sharing of sensitive data',
        ],
      },
    ],
  },
  {
    id: 19,
    title: 'Social Media Policy',
    intro: 'Online activity should never damage the organisation’s reputation or expose confidential information.',
    blocks: [
      {
        heading: 'Core principle',
        body: 'Confidential information must never be shared publicly.',
      },
    ],
  },
  {
    id: 20,
    title: 'Performance Management Policy',
    intro: 'Managers should review performance regularly and support ongoing development.',
    blocks: [
      {
        heading: 'This may include',
        bullets: ['Appraisals', 'Feedback sessions', 'Development plans'],
      },
    ],
  },
  {
    id: 21,
    title: 'Training and Development Policy',
    intro: 'Employees should be encouraged to build skills and participate in relevant training.',
    blocks: [
      {
        heading: 'Training may include',
        bullets: ['Professional development', 'Compliance training', 'Leadership training'],
      },
    ],
  },
  {
    id: 22,
    title: 'Disciplinary Policy',
    intro: 'Where conduct or performance falls below the required standard, disciplinary procedures may be followed.',
    blocks: [
      {
        heading: 'Possible outcomes',
        bullets: ['Verbal warning', 'Written warning', 'Final warning', 'Dismissal'],
        body: 'Employees should be given the opportunity to respond to allegations.',
      },
    ],
  },
  {
    id: 23,
    title: 'Grievance Policy',
    intro: 'Employees should be able to raise workplace concerns through a clear and fair grievance process.',
    blocks: [
      {
        heading: 'Grievances may relate to',
        bullets: ['Working conditions', 'Management decisions', 'Colleague behaviour'],
        body: 'All grievances should be investigated fairly.',
      },
    ],
  },
  {
    id: 24,
    title: 'Whistleblowing Policy',
    intro: 'Employees should feel able to report serious wrongdoing in good faith without fear of retaliation.',
    blocks: [
      {
        heading: 'Reports may include concerns about',
        bullets: ['Fraud', 'Illegal activity', 'Unsafe practices'],
      },
    ],
  },
  {
    id: 25,
    title: 'Termination of Employment',
    intro: 'Employment may end through different routes, each of which should be handled fairly and lawfully.',
    blocks: [
      {
        heading: 'Employment may end through',
        bullets: ['Resignation', 'Dismissal', 'Redundancy', 'Mutual agreement'],
        body: 'Notice periods should be clearly defined in employment contracts.',
      },
    ],
  },
  {
    id: 26,
    title: 'Policy Review',
    intro: 'Policies should be reviewed regularly to reflect legal updates, business changes, and good practice.',
    blocks: [
      {
        heading: 'Review expectation',
        body: 'These policies should be reviewed periodically to ensure they remain up to date with employment legislation and organisational needs.',
      },
    ],
  },
  {
    id: 27,
    title: 'Employee Acknowledgement',
    intro: 'Employees should confirm that they have read and understood the policies that apply to them.',
    blocks: [
      {
        heading: 'Acknowledgement',
        body: 'Acknowledgement confirms that the employee understands and agrees to follow the organisation’s workplace rules and expectations.',
      },
    ],
  },
];

function SectionBlock({ block }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h4 className="text-sm font-semibold uppercase tracking-wide text-indigo-700">{block.heading}</h4>
      {block.highlight && (
        <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3 text-sm font-medium text-indigo-900">
          {block.highlight}
        </div>
      )}
      {block.body && <p className="mt-3 text-sm leading-7 text-gray-600">{block.body}</p>}
      {block.bullets?.length > 0 && (
        <ul className="mt-3 space-y-2 text-sm leading-7 text-gray-600">
          {block.bullets.map((item) => (
            <li key={item} className="flex gap-3">
              <span className="mt-2 h-2 w-2 rounded-full bg-indigo-500" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function UKPolicies({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4">
      <div className="flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-slate-100 shadow-2xl ring-1 ring-slate-200">
        <div className="border-b border-slate-200 bg-white px-6 py-5 sm:px-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-4">
              <div className="inline-flex w-fit rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">
                Human Resources Department
              </div>
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                  UK HR & Workplace Policies
                </h2>
                <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-600 sm:text-base">
                  A clear and presentable workplace policy checklist for UK organisations, designed to support consistent standards,
                  fair treatment of employees, and alignment with core employment obligations.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              Close document
            </button>
          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Document Version</div>
              <div className="mt-1 text-base font-semibold text-slate-900">1.0</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Effective Date</div>
              <div className="mt-1 text-base font-semibold text-slate-900">14 March 2026</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Next Review Date</div>
              <div className="mt-1 text-base font-semibold text-slate-900">14 March 2027</div>
            </div>
          </div>
        </div>

        <div className="overflow-y-auto px-6 py-6 sm:px-8 sm:py-8">
          <section className="rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-slate-900 px-6 py-6 text-white shadow-lg sm:px-8">
            <h3 className="text-lg font-semibold">Purpose</h3>
            <p className="mt-3 max-w-4xl text-sm leading-7 text-indigo-50 sm:text-base">
              This document provides a comprehensive checklist of HR and workplace policies for organisations operating in the
              United Kingdom. It is intended to help employers build a professional internal framework for employment standards,
              policy consistency, and workplace compliance.
            </p>
            <p className="mt-3 max-w-4xl text-sm leading-7 text-indigo-100 sm:text-base">
              It can be used as a foundation for an employee handbook, internal policy manual, or HR governance review.
            </p>
          </section>

          <section className="mt-8 grid gap-6">
            {policySections.map((section) => (
              <article key={section.id} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-7">
                <div className="flex flex-col gap-4 border-b border-slate-100 pb-5 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-sm font-semibold uppercase tracking-[0.2em] text-indigo-600">Section {section.id}</div>
                    <h3 className="mt-2 text-xl font-semibold text-slate-900">{section.title}</h3>
                  </div>
                  <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">UK workplace guidance</div>
                </div>
                <p className="mt-5 text-sm leading-7 text-slate-600 sm:text-base">{section.intro}</p>
                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                  {section.blocks.map((block) => (
                    <SectionBlock key={`${section.id}-${block.heading}`} block={block} />
                  ))}
                </div>
              </article>
            ))}
          </section>

          <section className="mt-8 rounded-3xl border border-emerald-100 bg-emerald-50 px-6 py-6 sm:px-8">
            <h3 className="text-lg font-semibold text-emerald-900">Approval</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Approved By</div>
                <div className="mt-1 text-sm text-emerald-950">Human Resources Department</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Policy Version</div>
                <div className="mt-1 text-sm text-emerald-950">1.0</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Effective Date</div>
                <div className="mt-1 text-sm text-emerald-950">14 March 2026</div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
